import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, TextField, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TableSortLabel, Chip, CircularProgress, Alert,
    Stack, IconButton, Collapse, Select, FormControl, Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { API_BASE_URL } from '../components/api.js';
import { useAuth } from '../components/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) =>
    v !== null && v !== undefined
        ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
        : '—';

const MATCH_STATUS_LABEL = {
    0: { label: 'Sem match',  color: 'warning' },
    1: { label: 'Normal',     color: 'success' },
    2: { label: 'Fallback',   color: 'info' },
    3: { label: 'Manual',     color: 'secondary' },
};

function HeadCell({ id, label, order, orderBy, onSort }) {
    return (
        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700, py: 1 }}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => onSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );
}

// ─── Tabela SW ────────────────────────────────────────────────────────────────
function TabelaSW({ authenticatedFetch, logout }) {
    const [rows, setRows]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [erro, setErro]         = useState(null);
    const [orderBy, setOrderBy]   = useState('');
    const [order, setOrder]       = useState('asc');
    const [filtrosOpen, setFiltrosOpen] = useState(true);
    const [filtros, setFiltros]   = useState({
        data_inicio: '', data_fim: '', match_status: '', forma_pagamento: '', operador: '',
    });

    const buscar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const params = new URLSearchParams();
            if (orderBy) params.append('ordering', `${order === 'desc' ? '-' : ''}${orderBy}`);
            Object.entries(filtros).forEach(([k, v]) => { if (v !== '') params.append(k, v); });

            const res = await authenticatedFetch(`${API_BASE_URL}/api/pagamento-listar-sw/?${params}`);
            if (!res.ok) { if (res.status === 401) return logout(); throw new Error(); }
            setRows(await res.json());
        } catch { setErro('Erro ao carregar SW.'); }
        finally { setLoading(false); }
    }, [orderBy, order, filtros, authenticatedFetch, logout]);

    useEffect(() => { buscar(); }, [buscar]);

    const handleSort = (campo) => {
        setOrder(orderBy === campo && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(campo);
    };

    const cols = [
        { id: 'data',            label: 'Data' },
        { id: 'nr_cupom',        label: 'Cupom' },
        { id: 'operador',        label: 'Operador' },
        { id: 'forma_pagamento', label: 'Forma Pagamento' },
        { id: 'valor',           label: 'Valor' },
        { id: 'hora',            label: 'Hora' },
        { id: 'match_status',    label: 'Status' },
    ];

    return (
        <Paper variant="outlined" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>SW Fast</Typography>
                    <Typography variant="caption" color="text.secondary">{rows.length} registros</Typography>
                </Box>
                <IconButton size="small" onClick={() => setFiltrosOpen(o => !o)}>
                    <FilterListIcon fontSize="small" color={filtrosOpen ? 'primary' : 'inherit'} />
                </IconButton>
            </Stack>

            <Collapse in={filtrosOpen}>
                <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField label="Data início" name="data_inicio" type="date" value={filtros.data_inicio}
                        onChange={e => setFiltros(f => ({ ...f, data_inicio: e.target.value }))}
                        size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="Data fim" name="data_fim" type="date" value={filtros.data_fim}
                        onChange={e => setFiltros(f => ({ ...f, data_fim: e.target.value }))}
                        size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="Status" name="match_status" select value={filtros.match_status}
                        onChange={e => setFiltros(f => ({ ...f, match_status: e.target.value }))}
                        size="small" sx={{ minWidth: 130 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="0">Sem match</MenuItem>
                        <MenuItem value="1">Normal</MenuItem>
                        <MenuItem value="2">Fallback</MenuItem>
                        <MenuItem value="3">Manual</MenuItem>
                    </TextField>
                    <TextField label="Forma" value={filtros.forma_pagamento}
                        onChange={e => setFiltros(f => ({ ...f, forma_pagamento: e.target.value }))}
                        size="small" sx={{ minWidth: 160 }} />
                    <TextField label="Operador" value={filtros.operador}
                        onChange={e => setFiltros(f => ({ ...f, operador: e.target.value }))}
                        size="small" sx={{ minWidth: 140 }} />
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar} size="small">Filtrar</Button>
                    <Button variant="outlined" startIcon={<ClearIcon />} size="small"
                        onClick={() => { setFiltros({ data_inicio: '', data_fim: '', match_status: '', forma_pagamento: '', operador: '' }); setOrderBy(''); }}>
                        Limpar
                    </Button>
                </Box>
            </Collapse>

            {erro && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{erro}</Alert>}

            <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {cols.map(c => (
                                <HeadCell key={c.id} id={c.id} label={c.label}
                                    order={order} orderBy={orderBy} onSort={handleSort} />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={cols.length} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : rows.map(row => {
                            const status = MATCH_STATUS_LABEL[row.match_status] ?? { label: '?', color: 'default' };
                            return (
                                <TableRow key={row.id} hover
                                    sx={{ bgcolor: row.match_status === 0 ? 'warning.50' : 'inherit' }}>
                                    <TableCell>{row.data ?? '—'}</TableCell>
                                    <TableCell>{row.nr_cupom ?? '—'}</TableCell>
                                    <TableCell>{row.operador ?? '—'}</TableCell>
                                    <TableCell>{row.forma_pagamento ?? '—'}</TableCell>
                                    <TableCell align="right">R$ {fmt(row.valor)}</TableCell>
                                    <TableCell>{row.hora ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip label={status.label} color={status.color} size="small" />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

// ─── Tabela BIN ───────────────────────────────────────────────────────────────
function TabelaBin({ authenticatedFetch, logout }) {
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [erro, setErro]           = useState(null);
    const [orderBy, setOrderBy]     = useState('');
    const [order, setOrder]         = useState('asc');
    const [filtrosOpen, setFiltrosOpen] = useState(true);
    const [filtros, setFiltros]     = useState({ data_inicio: '', data_fim: '', sem_match: '', tipo: '' });

    // Cache de SW sem match por data para o dropdown
    const [swOpcoes, setSwOpcoes]   = useState({});  // { 'YYYY-MM-DD': [...] }
    const [salvando, setSalvando]   = useState({});   // { bin_id: true/false }

    const buscar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const params = new URLSearchParams();
            if (orderBy) params.append('ordering', `${order === 'desc' ? '-' : ''}${orderBy}`);
            Object.entries(filtros).forEach(([k, v]) => { if (v !== '') params.append(k, v); });

            const res = await authenticatedFetch(`${API_BASE_URL}/api/pagamento-listar-bin/?${params}`);
            if (!res.ok) { if (res.status === 401) return logout(); throw new Error(); }
            setRows(await res.json());
        } catch { setErro('Erro ao carregar BIN.'); }
        finally { setLoading(false); }
    }, [orderBy, order, filtros, authenticatedFetch, logout]);

    useEffect(() => { buscar(); }, [buscar]);

    // Carrega SW sem match para uma data (com cache)
    const carregarSwOpcoes = useCallback(async (data) => {
        if (swOpcoes[data]) return;
        try {
            const res = await authenticatedFetch(
                `${API_BASE_URL}/api/pagamento-sw-sem-match/?data=${data}`
            );
            if (!res.ok) return;
            const dados = await res.json();
            setSwOpcoes(prev => ({ ...prev, [data]: dados }));
        } catch { /* silencioso */ }
    }, [swOpcoes, authenticatedFetch]);

    const handleMatchChange = async (binId, data, swId) => {
        setSalvando(prev => ({ ...prev, [binId]: true }));
        try {
            const res = await authenticatedFetch(
                `${API_BASE_URL}/api/pagamento-match-manual/${binId}/`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sw_id: swId === '' ? null : swId }),
                }
            );
            if (!res.ok) throw new Error();
            // Atualiza a linha localmente para não recarregar tudo
            setRows(prev => prev.map(r => {
                if (r.id !== binId) return r;
                const sw = swId ? swOpcoes[data]?.find(s => s.id === swId) : null;
                return {
                    ...r,
                    match_id:       swId || null,
                    sw_hora:        sw?.hora ?? null,
                    sw_operador:    sw?.operador ?? null,
                    sw_nr_cupom:    sw?.nr_cupom ?? null,
                    sw_match_status: swId ? 3 : null,
                };
            }));
            // Invalida cache da data para refletir o SW que saiu do pool
            setSwOpcoes(prev => { const c = { ...prev }; delete c[data]; return c; });
        } catch {
            setErro(`Erro ao salvar match para BIN ${binId}.`);
        } finally {
            setSalvando(prev => ({ ...prev, [binId]: false }));
        }
    };

    const handleSort = (campo) => {
        setOrder(orderBy === campo && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(campo);
    };

    const cols = [
        { id: 'data',     label: 'Data' },
        { id: 'hora',     label: 'Hora BIN' },
        { id: 'tipo',     label: 'Tipo' },
        { id: 'sub_tipo', label: 'SubTipo' },
        { id: 'parcelas', label: 'Parcelas' },
        { id: 'valor',    label: 'Valor' },
        { id: 'match_id', label: 'Match SW' },
        { id: '_sw_info', label: 'Info SW' },
        { id: '_acao',    label: 'Ação' },
    ];

    return (
        <Paper variant="outlined">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>BIN (Cartão / PIX / Voucher)</Typography>
                    <Typography variant="caption" color="text.secondary">{rows.length} registros</Typography>
                </Box>
                <IconButton size="small" onClick={() => setFiltrosOpen(o => !o)}>
                    <FilterListIcon fontSize="small" color={filtrosOpen ? 'primary' : 'inherit'} />
                </IconButton>
            </Stack>

            <Collapse in={filtrosOpen}>
                <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField label="Data início" type="date" value={filtros.data_inicio}
                        onChange={e => setFiltros(f => ({ ...f, data_inicio: e.target.value }))}
                        size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="Data fim" type="date" value={filtros.data_fim}
                        onChange={e => setFiltros(f => ({ ...f, data_fim: e.target.value }))}
                        size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="Match" select value={filtros.sem_match}
                        onChange={e => setFiltros(f => ({ ...f, sem_match: e.target.value }))}
                        size="small" sx={{ minWidth: 130 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="1">Sem match</MenuItem>
                    </TextField>
                    <TextField label="Tipo" value={filtros.tipo}
                        onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))}
                        size="small" sx={{ minWidth: 120 }} />
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar} size="small">Filtrar</Button>
                    <Button variant="outlined" startIcon={<ClearIcon />} size="small"
                        onClick={() => { setFiltros({ data_inicio: '', data_fim: '', sem_match: '', tipo: '' }); setOrderBy(''); }}>
                        Limpar
                    </Button>
                </Box>
            </Collapse>

            {erro && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{erro}</Alert>}

            <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {cols.map(c =>
                                c.id.startsWith('_') ? (
                                    <TableCell key={c.id} sx={{ fontWeight: 700, py: 1, whiteSpace: 'nowrap' }}>
                                        {c.label}
                                    </TableCell>
                                ) : (
                                    <HeadCell key={c.id} id={c.id} label={c.label}
                                        order={order} orderBy={orderBy} onSort={handleSort} />
                                )
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={cols.length} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : rows.map(row => {
                            const temMatch = row.match_id !== null;
                            return (
                                <TableRow key={row.id} hover
                                    sx={{ bgcolor: !temMatch ? 'warning.50' : 'inherit' }}>
                                    <TableCell>{row.data ?? '—'}</TableCell>
                                    <TableCell>{row.hora ?? '—'}</TableCell>
                                    <TableCell>{row.tipo ?? '—'}</TableCell>
                                    <TableCell>{row.sub_tipo ?? '—'}</TableCell>
                                    <TableCell align="center">{row.parcelas ?? '—'}</TableCell>
                                    <TableCell align="right">R$ {fmt(row.valor)}</TableCell>

                                    {/* Dropdown de match manual */}
                                    <TableCell sx={{ minWidth: 220 }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={row.match_id ?? ''}
                                                displayEmpty
                                                onOpen={() => carregarSwOpcoes(row.data)}
                                                onChange={e => handleMatchChange(row.id, row.data, e.target.value || null)}
                                                disabled={salvando[row.id]}
                                                sx={{ fontSize: '0.8rem' }}
                                                renderValue={(val) => {
                                                    if (!val) return <em style={{ color: '#999' }}>Sem match</em>;
                                                    return `SW #${val}`;
                                                }}
                                            >
                                                <MenuItem value=""><em>Sem match</em></MenuItem>
                                                {/* SW atual (pode não estar no pool sem match) */}
                                                {row.match_id && !(swOpcoes[row.data] ?? []).find(s => s.id === row.match_id) && (
                                                    <MenuItem value={row.match_id}>
                                                        SW #{row.match_id} — {row.sw_hora} · R$ {fmt(row.valor)}
                                                    </MenuItem>
                                                )}
                                                {(swOpcoes[row.data] ?? []).map(sw => (
                                                    <MenuItem key={sw.id} value={sw.id}>
                                                        #{sw.id} · {sw.hora} · {sw.forma_pagamento} · R$ {fmt(sw.valor)} · {sw.operador}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>

                                    {/* Info resumida do SW vinculado */}
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {temMatch ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {row.sw_hora} · {row.sw_operador} · #{row.sw_nr_cupom}
                                            </Typography>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                        )}
                                    </TableCell>

                                    {/* Ação rápida: desvincular */}
                                    <TableCell>
                                        {temMatch && (
                                            <Tooltip title="Remover match">
                                                <IconButton size="small"
                                                    disabled={salvando[row.id]}
                                                    onClick={() => handleMatchChange(row.id, row.data, null)}>
                                                    <LinkOffIcon fontSize="small" color="warning" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!temMatch && (
                                            <Tooltip title="Selecione um SW no dropdown">
                                                <LinkIcon fontSize="small" color="disabled" />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

// ─── Página principal ──────────────────────────────────────────────────────────
export default function PagamentosVerificar() {
    const { authenticatedFetch, logout } = useAuth();

    return (
        <Box sx={{ p: 3 }}>
            <Box mb={3}>
                <Typography variant="h5" fontWeight={700}>Verificação de Pagamentos</Typography>
                <Typography variant="body2" color="text.secondary">
                    SW sem match aparece em amarelo. Use o dropdown na tabela BIN para vincular manualmente.
                </Typography>
            </Box>

            <TabelaSW authenticatedFetch={authenticatedFetch} logout={logout} />
            <TabelaBin authenticatedFetch={authenticatedFetch} logout={logout} />
        </Box>
    );
}